/**
 * Landing Module - ModuleCard Component
 * Kort for rettsområde-moduler med status-badge
 * 
 * Navigasjonslogikk:
 * - Autentisert bruker: Navigerer direkte til modulens sti
 * - Uautentisert bruker: Åpner info-modal
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  Info, FileText, CheckCircle, Zap, Shield, ChevronRight,
  Users, ShoppingBag, Building2, Briefcase, Home, Map, 
  ScrollText, Heart, Scale, Gavel, Lock, Sparkles
} from 'lucide-react';

const ICONS = {
  Info, FileText, CheckCircle, Zap, Shield,
  Users, ShoppingBag, Building2, Briefcase, Home, 
  Map, ScrollText, Heart, Scale, Gavel
};

const STATUS_CONFIG = {
  active: {
    badge: 'AKTIV',
    badgeClass: 'bg-[var(--success)] text-white',
    cardClass: 'cursor-pointer hover:-translate-y-1 hover:border-[var(--copper)]/40 hover:shadow-lg',
    iconClass: 'text-[var(--copper)]',
    opacity: 'opacity-100'
  },
  coming: {
    badge: 'KOMMER SNART',
    badgeClass: 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]',
    cardClass: 'cursor-default pointer-events-none',
    iconClass: 'text-[var(--text-muted)]',
    opacity: 'opacity-60'
  }
};

export function ModuleCard({ module, index, show, onClick, isAuthenticated: propIsAuth, compact = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Bruk prop hvis gitt, ellers sjekk auth context
  const isAuthenticated = propIsAuth !== undefined ? propIsAuth : !!user;
  
  const Icon = ICONS[module.icon] || FileText;
  const config = STATUS_CONFIG[module.status] || STATUS_CONFIG.coming;

  const handleClick = () => {
    if (module.status !== 'active') return;
    
    if (isAuthenticated && module.route) {
      // Autentisert bruker: Naviger direkte til modulen
      navigate(module.route);
    } else if (onClick) {
      // Uautentisert bruker: Åpne info-modal
      onClick(module.id);
    }
  };

  // Compact layout for 6-column grid
  if (compact) {
    return (
      <div
        onClick={handleClick}
        data-testid={`module-card-${module.id}`}
        className={`
          group relative p-3 rounded-lg bg-[var(--bg-surface)]/60 border border-[var(--border-subtle)]
          transition-all duration-300 ease-out
          ${module.status === 'active' ? 'cursor-pointer hover:border-[var(--copper)]/40 hover:shadow-md' : 'cursor-default'}
          ${config.opacity}
          ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
        `}
        style={{ 
          transitionDelay: show ? `${200 + index * 30}ms` : '0ms',
          ...(module.status === 'coming' ? { pointerEvents: 'none' } : {})
        }}
      >
        {/* Badges */}
        <div className="flex items-center gap-1 mb-2">
          {module.badge && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-bold uppercase rounded bg-[var(--copper)] text-white">
              <Sparkles className="w-2 h-2" />
              {module.badge}
            </span>
          )}
          <span className={`px-1.5 py-0.5 text-[8px] font-semibold uppercase rounded ${config.badgeClass}`}>
            {module.status === 'active' ? '' : config.badge}
          </span>
        </div>
        
        {/* Icon + Title */}
        <div className="flex items-center gap-2">
          <div className={`
            w-7 h-7 rounded flex items-center justify-center flex-shrink-0
            ${module.status === 'active' ? 'bg-[var(--copper)]/10' : 'bg-[var(--bg-highlight)]'}
          `}>
            <Icon className={`w-3.5 h-3.5 ${config.iconClass}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-[var(--text-main)] text-[11px] truncate">
              {module.title}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      data-testid={`module-card-${module.id}`}
      className={`
        group relative p-5 rounded-xl bg-[var(--bg-surface)]/60 border border-[var(--border-subtle)]
        transition-all duration-300 ease-out overflow-visible
        ${config.cardClass}
        ${config.opacity}
        ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      style={{ 
        transitionDelay: show ? `${300 + index * 40}ms` : '0ms',
        ...(module.status === 'coming' ? { pointerEvents: 'none' } : {})
      }}
    >
      {/* Master Badge - øverst til høyre */}
      {module.badge && (
        <div className="absolute -top-3 right-3 z-10">
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded bg-[var(--copper)] text-white">
            <Sparkles className="w-3 h-3" />
            {module.badge}
          </span>
        </div>
      )}
      
      {/* Coming Soon Badge - for kommende moduler */}
      {module.status === 'coming' && (
        <div className="absolute -top-3 right-3 z-10">
          <span className="inline-flex items-center px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded bg-[var(--text-muted)]/30 text-[var(--text-muted)]">
            KOMMER SNART
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex items-start gap-3 pt-2">
        <div className={`
          w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0
          ${module.status === 'active' ? 'bg-[var(--copper)]/10' : 'bg-[var(--bg-highlight)]'}
        `}>
          <Icon className={`w-5 h-5 ${config.iconClass}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-[var(--text-main)] text-sm">
              {module.title}
            </h3>
            {/* Grønn aktiv-prikk med puls-animasjon */}
            {module.status === 'active' && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-2">
            {module.description}
          </p>
          {/* Short Sell Text */}
          {module.shortSell && module.status === 'active' && (
            <p className="text-[11px] text-[var(--copper)] font-medium">
              {module.shortSell}
            </p>
          )}
        </div>

        {module.status === 'active' && (
          <ChevronRight className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--copper)]" />
        )}

        {module.status === 'coming' && (
          <Lock className="w-3.5 h-3.5 text-[var(--text-muted)] flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

// Original MenuButton for info items
export function MenuButton({ item, onClick, index, show }) {
  const Icon = ICONS[item.icon] || Info;
  
  return (
    <button
      onClick={onClick}
      data-testid={`menu-btn-${item.id}`}
      className={`group relative w-full p-3 rounded-lg text-left bg-[var(--bg-surface)]/40 border border-[var(--border-subtle)]
        transition-all duration-300 ease-out hover:bg-[var(--bg-surface)] hover:border-[var(--copper)]/20
        ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
      `}
      style={{ transitionDelay: show ? `${500 + index * 50}ms` : '0ms' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[var(--copper)]" />
          <span className="font-medium text-[var(--text-main)] text-sm">{item.title}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--text-muted)] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-[var(--copper)]" />
      </div>
    </button>
  );
}

export default ModuleCard;
